import { useEffect, useState } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { AlertTriangle, Check, Cpu, Edit2, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  useAIProviders,
  useRotateProviderKey,
  useTestProvider,
  useUpdateProvider,
  type AIProvider,
} from '@/hooks/useAIProviders'
import { AdminAuditFeed, type AuditEntry } from '@/components/admin/AdminAuditFeed'

// ─── Helpers ────────────────────────────────────────────────────────────────

function renderProviderAction(entry: AuditEntry): string {
  const map: Record<string, string> = {
    key_rotate: 'rotated API key',
    key_test: 'tested API key',
    provider_enable: 'enabled provider',
    provider_disable: 'disabled provider',
    provider_base_url_update: 'updated base URL override',
  }
  return map[entry.action] ?? entry.action
}

function shortFingerprint(fp: string | null): string {
  if (!fp) return '—'
  if (fp.length <= 10) return fp
  return `${fp.slice(0, 10)}…`
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString()
  } catch {
    return iso
  }
}

// ─── Edit modal ─────────────────────────────────────────────────────────────

interface EditModalProps {
  provider: AIProvider
  open: boolean
  onClose: () => void
}

function EditProviderModal({ provider, open, onClose }: EditModalProps) {
  const [enabled, setEnabled] = useState<boolean>(provider.enabled ?? true)
  const [baseUrlOverride, setBaseUrlOverride] = useState<string>(
    provider.baseUrlOverride ?? '',
  )
  const [apiKey, setApiKey] = useState<string>('')

  const rotate = useRotateProviderKey()
  const test = useTestProvider()
  const update = useUpdateProvider()

  // Reset form state whenever we open the modal for a (possibly new) provider.
  useEffect(() => {
    if (open) {
      setEnabled(provider.enabled ?? true)
      setBaseUrlOverride(provider.baseUrlOverride ?? '')
      setApiKey('')
      rotate.reset?.()
      test.reset?.()
      update.reset?.()
    }
  // intentionally omits rotate/test/update from deps — their .reset() is stable
  }, [open, provider.name])

  const hasNewKey = apiKey.trim().length > 0

  function handleTest() {
    test.mutate({ name: provider.name })
  }

  function handleSave() {
    if (hasNewKey) {
      rotate.mutate(
        { name: provider.name, apiKey: apiKey.trim() },
        {
          onSuccess: () => {
            // Clear the key field after a successful rotate; keep modal open
            // so the operator sees the fresh fingerprint via cache refetch.
            setApiKey('')
          },
        },
      )
    } else {
      update.mutate(
        {
          name: provider.name,
          enabled,
          baseUrlOverride: baseUrlOverride.trim() ? baseUrlOverride.trim() : null,
        },
        {
          onSuccess: () => onClose(),
        },
      )
    }
  }

  // Derive a user-facing error string from a mutation's error payload.
  function errorMessage(err: unknown): string {
    if (!err) return 'Request failed.'
    if (err instanceof Error) return err.message
    return String(err)
  }

  const pending = rotate.isPending || update.isPending
  const testResult = test.data

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-background shadow-xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
          <div className="flex items-start justify-between gap-4 border-b px-6 py-4">
            <div>
              <DialogPrimitive.Title className="text-base font-semibold">
                Edit {provider.name}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-xs text-muted-foreground mt-0.5">
                Current fingerprint:{' '}
                <span className="font-mono">{shortFingerprint(provider.keyFingerprint)}</span>
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </DialogPrimitive.Close>
          </div>

          <div className="flex flex-col gap-4 px-6 py-4">
            {/* Enabled toggle */}
            <div className="flex items-center justify-between gap-3">
              <label htmlFor="provider-enabled" className="text-sm font-medium">
                Enabled
              </label>
              <Switch
                id="provider-enabled"
                aria-label="Enabled"
                checked={enabled}
                onCheckedChange={setEnabled}
              />
            </div>

            {/* Base URL override */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="provider-base-url" className="text-sm font-medium">
                Base URL override{' '}
                <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <Input
                id="provider-base-url"
                value={baseUrlOverride}
                onChange={(e) => setBaseUrlOverride(e.target.value)}
                placeholder="https://api.example.com/v1"
              />
            </div>

            {/* API key (paste to rotate) */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="provider-api-key" className="text-sm font-medium">
                API key{' '}
                <span className="text-muted-foreground font-normal">
                  (paste new key to rotate — leave blank to keep current)
                </span>
              </label>
              <Input
                id="provider-api-key"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="sk-..."
                autoComplete="off"
              />
            </div>

            {/* Test / rotate / update result banners */}
            {test.isSuccess && testResult && (
              <div
                className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${
                  testResult.ok
                    ? 'border-green-500/40 bg-green-500/10 text-green-300'
                    : 'border-red-500/40 bg-red-500/10 text-red-300'
                }`}
                role="status"
              >
                {testResult.ok ? (
                  <Check className="h-4 w-4 shrink-0" />
                ) : (
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                )}
                <span>
                  {testResult.ok
                    ? `Test OK — ${testResult.wallClockMs} ms`
                    : `Test failed: ${testResult.error ?? 'unknown error'}`}
                </span>
              </div>
            )}

            {test.isError && (
              <div
                className="flex items-start gap-2 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300"
                role="alert"
              >
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>Test request failed: {errorMessage(test.error)}</span>
              </div>
            )}

            {rotate.isError && (
              <div
                className="flex items-start gap-2 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300"
                role="alert"
              >
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>Rotate failed: {errorMessage(rotate.error)}</span>
              </div>
            )}

            {rotate.isSuccess && (
              <div
                className="flex items-start gap-2 rounded-md border border-green-500/40 bg-green-500/10 px-3 py-2 text-xs text-green-300"
                role="status"
              >
                <Check className="h-4 w-4 shrink-0" />
                <span>Key rotated.</span>
              </div>
            )}

            {update.isError && (
              <div
                className="flex items-start gap-2 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300"
                role="alert"
              >
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>Save failed: {errorMessage(update.error)}</span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap justify-end gap-2 border-t px-6 py-4">
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={test.isPending}
              type="button"
            >
              {test.isPending ? 'Testing…' : 'Test Current Key'}
            </Button>
            <Button variant="outline" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={pending} type="button">
              {hasNewKey
                ? pending
                  ? 'Rotating…'
                  : 'Save & Rotate'
                : pending
                  ? 'Saving…'
                  : 'Save Settings'}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

// ─── Table ──────────────────────────────────────────────────────────────────

interface ProvidersTableProps {
  providers: AIProvider[]
  onEdit: (provider: AIProvider) => void
}

function ProvidersTable({ providers, onEdit }: ProvidersTableProps) {
  return (
    <div className="rounded-lg border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/20 text-muted-foreground">
              <th className="px-4 py-3 text-left font-medium">Name</th>
              <th className="px-4 py-3 text-left font-medium">Enabled</th>
              <th className="px-4 py-3 text-left font-medium hidden md:table-cell">
                Key fingerprint
              </th>
              <th className="px-4 py-3 text-center font-medium">Last test</th>
              <th className="px-4 py-3 text-left font-medium hidden lg:table-cell">
                Last rotated
              </th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {providers.map((provider) => {
              const isEnabled = provider.enabled ?? false
              const testState =
                provider.lastTestOk === null
                  ? '—'
                  : provider.lastTestOk
                    ? '✓'
                    : '✗'
              return (
                <tr
                  key={provider.name}
                  className="border-b last:border-0 hover:bg-muted/30 transition-colors"
                >
                  <td className="px-4 py-3 font-medium font-mono">{provider.name}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1.5 text-xs font-medium ${
                        isEnabled ? 'text-green-400' : 'text-muted-foreground'
                      }`}
                    >
                      <span
                        className={`h-2 w-2 rounded-full ${
                          isEnabled ? 'bg-green-400' : 'bg-muted-foreground/50'
                        }`}
                      />
                      {isEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className="font-mono text-xs text-muted-foreground">
                      {shortFingerprint(provider.keyFingerprint)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      title={provider.lastTestError ?? undefined}
                      className={`text-sm font-medium ${
                        provider.lastTestOk === null
                          ? 'text-muted-foreground'
                          : provider.lastTestOk
                            ? 'text-green-400'
                            : 'text-red-400'
                      }`}
                    >
                      {testState}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-xs text-muted-foreground">
                    {formatDate(provider.lastRotatedAt)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => onEdit(provider)}
                    >
                      <Edit2 className="h-3.5 w-3.5 mr-1" />
                      Edit
                    </Button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── Page ───────────────────────────────────────────────────────────────────

export function AIProvidersPage() {
  const providersQuery = useAIProviders()
  const [editingProvider, setEditingProvider] = useState<AIProvider | null>(null)

  const providers = providersQuery.data?.providers ?? []

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Cpu className="h-6 w-6 text-muted-foreground mt-1" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight">AI Providers</h1>
            <p className="text-muted-foreground text-sm mt-0.5">
              Configure and monitor AI provider connections.
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button disabled aria-disabled="true">
            <Plus className="h-4 w-4 mr-2" />
            Add Provider
          </Button>
          <p className="text-xs text-muted-foreground">
            Provider catalogue is fixed in this release.
          </p>
        </div>
      </div>

      {/* Body */}
      {providersQuery.isLoading && (
        <div className="rounded-lg border px-4 py-8 text-center text-sm text-muted-foreground">
          Loading providers…
        </div>
      )}

      {providersQuery.isError && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>Failed to load providers.</span>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => providersQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      )}

      {!providersQuery.isLoading && !providersQuery.isError && providers.length === 0 && (
        <div className="rounded-lg border px-4 py-8 text-center text-sm text-muted-foreground">
          No providers registered.
        </div>
      )}

      {!providersQuery.isLoading && !providersQuery.isError && providers.length > 0 && (
        <ProvidersTable providers={providers} onEdit={setEditingProvider} />
      )}

      {/* Audit feed */}
      <div className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold tracking-tight text-muted-foreground uppercase">
          Recent provider activity
        </h2>
        <AdminAuditFeed
          feedUrl="/admin/router/audit"
          renderAction={renderProviderAction}
        />
      </div>

      {/* Edit modal */}
      {editingProvider && (
        <EditProviderModal
          provider={editingProvider}
          open={Boolean(editingProvider)}
          onClose={() => setEditingProvider(null)}
        />
      )}
    </div>
  )
}
